import { useQuery } from '@tanstack/react-query';
import { format, isSameDay, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { transportTypeOptions } from '@/constants/TransportTypeOptions';
import api from '../../services/apiSgft';
import { Coverage, CoverageStructure } from '../../types/VacationRegister';
import { addInclusiveCalendarDays, civilDate, removeTimeZone } from '../../utils/formatDate';
import { getDateRange } from '../../utils/getDateRange';
import {
	normalizeDateToYyyyMmDd,
	normalizePersistedTransportBundle,
} from '../../utils/normalizeVacationCoverTransportData';
import { ControlledDateRangePicker } from '../atoms/ControlledDateRangePicker';
import ControlledSimpleSelect, {
	Values,
} from '../atoms/ControlledSimpleSelect';
import TrashButton from '../atoms/TrashButton';
import TransportSection, { TransportDay } from './TransportSection';

interface Props {
	employees: { id: string; name: string }[];
	structures: CoverageStructure[] | undefined;
	index: number;
	idx: number;
	vacationCovers?: { startDate: Date; endDate: Date }[];
	remove: UseFieldArrayRemove;
}

function hasSavedEmployeeNotInOptions(
	options: Values[],
	employeeId: unknown,
	employeeName: unknown,
): boolean {
	if (!employeeId || !employeeName) return false;

	const id = String(employeeId);
	return !options.some((option) => String(option.value) === id);
}

function isEmployeeInOptions(
	coverageEmployeeId: number,
	options: Values[],
): boolean {
	const id = String(coverageEmployeeId);
	return options.some((option) => String(option.value) === id);
}

function resolveEmployeeSelectLabel(
	options: Values[],
	selected: unknown,
	savedEmployeeId: unknown,
	savedEmployeeName: unknown,
): string {
	if (selected === '' || selected == null) return '';

	const id = String(selected);
	const fromOptions = options.find((option) => String(option.value) === id);
	if (fromOptions) return String(fromOptions.label);

	if (
		savedEmployeeId != null &&
		String(savedEmployeeId) === id &&
		savedEmployeeName
	) {
		return String(savedEmployeeName);
	}

	return '';
}

const VacationCoverPlanPeriod = ({
	index,
	employees,
	idx,
	vacationCovers,
	remove,
	structures,
}: Props) => {
	const [employeeValues, setEmployeeValues] = useState<Values[]>([]);
	const [notAllowedPeriods, setNotAllowedPeriods] = useState<
		{
			startDate: Date;
			endDate: Date;
		}[]
	>([]);
	const [isTransportExpanded, setIsTransportExpanded] = useState(true);
	const [transportDays, setTransportDays] = useState<TransportDay[]>([]);
	const [passengerInfo, setPassengerInfo] = useState({
		address: {
			cep: '',
			uf: '',
			city: '',
			neighborhood: '',
			street: '',
			number: '',
			complement: '',
		},
		phone: '',
	});
	const [repeatLocation, setRepeatLocation] = useState<{
		[key: string]: boolean;
	}>({});
	const { control, getValues, setValue, watch } = useFormContext();

	const employeeIdField = `vacationPeriods.${index}.vacationCovers.${idx}.employeeId`;
	const employeeNameField = `vacationPeriods.${index}.vacationCovers.${idx}.name`;
	const watchEmployeeId = watch(employeeIdField);
	const watchEmployeeName = watch(employeeNameField);

	const employeeSelectOptions = useMemo(
		() =>
			employees.map((el) => ({
				value: el.id,
				label: el.name,
			})),
		[employees],
	);

	const renderEmployeeSelectValue = useCallback(
		(selected: unknown, options: Values[]) =>
			resolveEmployeeSelectLabel(
				options,
				selected,
				watchEmployeeId,
				watchEmployeeName,
			),
		[watchEmployeeId, watchEmployeeName],
	);

	const showFromAnotherAreaEmployeeSelect =
		employeeValues.length > 0 ||
		hasSavedEmployeeNotInOptions(
			employeeValues,
			watchEmployeeId,
			watchEmployeeName,
		);

	const coverageTypeValues = [
		{ value: Coverage.STANDARD, label: 'COBERTURA PELA MESMA ESTRUTURA' },
		{
			value: Coverage.FROM_ANOTHER_AREA,
			label: 'COBERTURA POR OUTRA ESTRUTURA',
		},
		{ value: Coverage.THIRD_PARTY, label: 'COBERTURA POR TERCEIROS' },
		{ value: Coverage.VIA_CLASS_CHANGE, label: 'COBERTURA VIA FERISTA' },
		{ value: Coverage.NO_NEED, label: 'NÃO NECESSITA DE COBERTURA' },
	];

	const outOfTeamCoverage = [
		Coverage.NO_NEED,
		Coverage.THIRD_PARTY,
		Coverage.FROM_ANOTHER_AREA,
		Coverage.VIA_CLASS_CHANGE,
	];
	const periodStartDate = civilDate(
		getValues(`vacationPeriods.${index}.startDate`),
	);
	const totalDays = getValues(`vacationPeriods.${index}.totalDays`);
	const periodEndDate = addInclusiveCalendarDays(periodStartDate, totalDays);
	const coverageTypeField = `vacationPeriods.${index}.vacationCovers.${idx}.coverageType`;
	const structureName = `vacationPeriods.${index}.vacationCovers.${idx}.structure.id`;
	const needTransportField = `vacationPeriods.${index}.vacationCovers.${idx}.needTransport`;
	const startDateField = `vacationPeriods.${index}.vacationCovers.${idx}.startDate`;
	const endDateField = `vacationPeriods.${index}.vacationCovers.${idx}.endDate`;
	const transportDataField = `vacationPeriods.${index}.vacationCovers.${idx}.transportData`;

	const fetchAvailableCoverDays = useCallback(
		async (coverageEmployeeId: number | null) => {
			const currentPeriodStartDate = civilDate(
				getValues(`vacationPeriods.${index}.startDate`),
			);
			const currentTotalDays = getValues(`vacationPeriods.${index}.totalDays`);
			const currentPeriodEndDate = addInclusiveCalendarDays(
				currentPeriodStartDate,
				currentTotalDays,
			);
			const fullPeriod = getDateRange(
				currentPeriodStartDate,
				currentPeriodEndDate,
			);
			let daysToBeCovered: Array<Date> = [];
			if (!coverageEmployeeId) {
				daysToBeCovered =
					vacationCovers?.flatMap((vacationCover) => {
						return getDateRange(
							removeTimeZone(new Date(vacationCover.startDate)),
							removeTimeZone(new Date(vacationCover.endDate)),
						);
					}) ?? [];
			} else {
				const coverageType = getValues(coverageTypeField);
				const eligibleOptions =
					coverageType === Coverage.FROM_ANOTHER_AREA
						? employeeValues
						: employeeSelectOptions;

				if (!isEmployeeInOptions(coverageEmployeeId, eligibleOptions)) {
					setNotAllowedPeriods([]);
					return;
				}

				const employeeId = getValues('employeeId');
				const currentStructureName = `vacationPeriods.${index}.vacationCovers.${idx}.structure.id`;
				const response = await api.get('/vacation/coverage/availableDates', {
					params: {
						employeeId,
						coverageEmployeeId,
						startDate: currentPeriodStartDate,
						endDate: currentPeriodEndDate,
						anotherStructureId: getValues(currentStructureName),
						isFromAnotherStructure: true,
					},
				});
				daysToBeCovered = response.data.map((date: string) => parseISO(date));
			}
			const newNotAllowedPeriods: Array<{
				startDate: Date;
				endDate: Date;
			}> = [];
			fullPeriod.forEach((day) => {
				if (
					!daysToBeCovered.some((dayDoBeCovered) =>
						isSameDay(day, dayDoBeCovered),
					)
				) {
					newNotAllowedPeriods.push({
						startDate: day,
						endDate: day,
					});
				}
			});
			setNotAllowedPeriods(newNotAllowedPeriods);
		},
		[
			getValues,
			index,
			idx,
			vacationCovers,
			coverageTypeField,
			employeeValues,
			employeeSelectOptions,
		],
	);

	const structuresValues = structures
		? structures.map((structure) => {
				return {
					value: structure.id,
					label: structure.name,
				};
			})
		: undefined;

	const watchedPeriodStartDate = watch(`vacationPeriods.${index}.startDate`);
	const watchedPeriodEndDate = watch(`vacationPeriods.${index}.endDate`);

	const watchCoverageType = watch(coverageTypeField);
	const watchStructureName = watch(structureName);

	const handleStructureSelectValue = useCallback(() => {
		const structureEmployeeSelectValues = structures
			? structures
					.filter((structure) => structure.id === watchStructureName)
					.flatMap((structure) =>
						structure.employees.map((employee) => ({
							value: employee.id,
							label: employee.name,
						})),
					)
			: [];

		setEmployeeValues(structureEmployeeSelectValues);
	}, [structures, watchStructureName]);

	useEffect(() => {
		if (watchedPeriodStartDate && watchedPeriodEndDate) {
			const employeeId = getValues(employeeIdField);
			if (employeeId) {
				fetchAvailableCoverDays(Number(employeeId));
			} else {
				fetchAvailableCoverDays(null);
			}
		}
	}, [
		watchedPeriodStartDate,
		watchedPeriodEndDate,
		employeeIdField,
		getValues,
		fetchAvailableCoverDays,
	]);

	useEffect(() => {
		handleStructureSelectValue();
	}, [handleStructureSelectValue]);

	useEffect(() => {
		const employeeId = getValues(employeeIdField);
		const coverageType = getValues(coverageTypeField);

		if (!coverageType && !employeeId) {
			setValue(coverageTypeField, Coverage.STANDARD);
		}

		if (employeeId) {
			fetchAvailableCoverDays(Number(employeeId));
		} else {
			fetchAvailableCoverDays(null);
		}
	}, [
		employeeIdField,
		coverageTypeField,
		getValues,
		setValue,
		fetchAvailableCoverDays,
	]);

	const transportOptions = [
		{ value: 'true', label: 'Sim' },
		{ value: 'false', label: 'Não' },
	];

	const watchNeedTransport = watch(needTransportField);
	const transportNeeded = watchNeedTransport === true;
	const watchStartDate = watch(startDateField);
	const watchEndDate = watch(endDateField);

	const { data: locations = [] } = useQuery(
		['work-stations-all'],
		async () => {
			const response = await api.get('/work-station/all');
			return response.data.map((ws: { id: number; name: string }) => ({
				value: ws.id,
				label: ws.name,
			}));
		},
		{
			retry: false,
			staleTime: Infinity,
		},
	);

	useEffect(() => {
		const fetchEmployeeData = async () => {
			if (
				watchEmployeeId &&
				(watchCoverageType === Coverage.STANDARD ||
					watchCoverageType === Coverage.FROM_ANOTHER_AREA)
			) {
				try {
					const { passengerPhone } = normalizePersistedTransportBundle(
						getValues(transportDataField),
					);
					const response = await api.get('/vacation/coverage/employee-data', {
						params: { employeeId: watchEmployeeId },
					});
					const data = response.data;
					setPassengerInfo({
						address: {
							cep: data.zipCode || '',
							uf: data.state || '',
							city: data.city || '',
							neighborhood: data.neighborhood || '',
							street: data.street || '',
							number: data.houseNumber || '',
							complement: data.addressDetails || '',
						},
						phone: passengerPhone,
					});
				} catch (error) {
					console.error('Failed to load coverage employee data:', error);
				}
			}
		};
		fetchEmployeeData();
	}, [watchEmployeeId, watchCoverageType, transportDataField, getValues]);

	useEffect(() => {
		if (!transportNeeded || !watchStartDate || !watchEndDate) {
			setTransportDays([]);
			setRepeatLocation({});
			return;
		}

		const start = removeTimeZone(new Date(watchStartDate));
		const end = removeTimeZone(new Date(watchEndDate));
		const days = getDateRange(start, end);
		const { transportDays: persisted } = normalizePersistedTransportBundle(
			getValues(transportDataField),
		);

		const transportDaysData: TransportDay[] = days.map((day, dayIdx) => {
			const dateStr = format(day, 'yyyy-MM-dd');
			const existing = persisted.find(
				(d) => normalizeDateToYyyyMmDd(d.date) === dateStr,
			);
			if (existing) {
				return existing;
			}
			return {
				id: `day-${dayIdx}-${day.getTime()}`,
				date: dateStr,
				entryTime: '',
				exitTime: '',
				transportType: null,
				location: null,
			};
		});
		setTransportDays(transportDaysData);
		const initialRepeatLocation: { [key: string]: boolean } = {};
		transportDaysData.forEach((day) => {
			initialRepeatLocation[day.id] = false;
		});
		setRepeatLocation(initialRepeatLocation);
	}, [
		watchStartDate,
		watchEndDate,
		transportNeeded,
		transportDataField,
		getValues,
	]);

	const handleUpdateTransportDay = (
		id: string,
		field: keyof TransportDay,
		value: string | number | null,
	) => {
		setTransportDays((prevDays) =>
			prevDays.map((day) => (day.id === id ? { ...day, [field]: value } : day)),
		);
	};

	const handleRepeatLocationChange = (dayId: string, checked: boolean) => {
		setRepeatLocation((prev) => ({
			...prev,
			[dayId]: checked,
		}));
	};

	const handlePhoneChange = (value: string) => {
		setPassengerInfo((prev) => ({ ...prev, phone: value }));
	};

	useEffect(() => {
		if (!transportNeeded) {
			setValue(transportDataField, undefined, { shouldDirty: false });
			return;
		}
		if (transportDays.length > 0) {
			setValue(
				transportDataField,
				{
					transportDays,
					passengerInfo,
				},
				{ shouldDirty: false },
			);
		}
	}, [
		transportDays,
		passengerInfo,
		transportNeeded,
		transportDataField,
		setValue,
	]);

	return (
		<div className="mb-5 flex flex-col gap-2">
			<div className="grid grid-cols-3 gap-5 items-end">
				<ControlledDateRangePicker
					startDateName={`vacationPeriods.${index}.vacationCovers.${idx}.startDate`}
					endDateName={`vacationPeriods.${index}.vacationCovers.${idx}.endDate`}
					title="Data"
					startFrom={periodStartDate}
					minDate={periodStartDate}
					maxDate={periodEndDate}
					disabledDates={notAllowedPeriods}
				/>
				<ControlledSimpleSelect
					width="100%"
					title={'Tipo'}
					onChange={(e) => {
						const newValue = String(e.target.value);
						setValue(coverageTypeField, newValue);
						setValue(employeeIdField, null);
						if (newValue !== Coverage.FROM_ANOTHER_AREA) {
							setValue(structureName, null);
						}
						fetchAvailableCoverDays(null);
					}}
					values={coverageTypeValues}
					name={coverageTypeField}
					control={control}
					changeItemColor
					changeItems={outOfTeamCoverage}
				/>
				<ControlledSimpleSelect
					width="100%"
					title="Estrutura"
					values={structuresValues}
					name={structureName}
					control={control}
					disabled={watchCoverageType !== Coverage.FROM_ANOTHER_AREA}
					onChange={() => {
						handleStructureSelectValue();
					}}
					disabledStyle="gray-filled"
				/>
			</div>

			<div className="grid grid-cols-3 gap-5">
				<div className="col-span-2">
					{watchCoverageType === Coverage.FROM_ANOTHER_AREA &&
					showFromAnotherAreaEmployeeSelect ? (
						<ControlledSimpleSelect
							width="100%"
							title="Colaborador"
							values={employeeValues}
							renderValue={(selected) =>
								renderEmployeeSelectValue(selected, employeeValues)
							}
							onChange={(e) => {
								const newValue = String(e.target.value);
								setValue(employeeIdField, e.target.value);
								fetchAvailableCoverDays(Number(newValue));
							}}
							name={employeeIdField}
							control={control}
						/>
					) : watchCoverageType === Coverage.STANDARD ? (
						<ControlledSimpleSelect
							width="100%"
							title="Colaborador"
							values={employeeSelectOptions}
							renderValue={(selected) =>
								renderEmployeeSelectValue(selected, employeeSelectOptions)
							}
							onChange={(e) => {
								const newValue = String(e.target.value);
								setValue(employeeIdField, e.target.value);
								setValue(coverageTypeField, Coverage.STANDARD);
								fetchAvailableCoverDays(Number(newValue));
							}}
							name={employeeIdField}
							control={control}
						/>
					) : (
						<ControlledSimpleSelect
							width="100%"
							title="Colaborador"
							values={[]}
							name={employeeIdField}
							control={control}
							disabled
						/>
					)}
				</div>
				<div className="col-span-1">
					<div className="grid grid-cols-3 gap-2 items-end">
						<div className="col-span-2">
							<ControlledSimpleSelect
								width="100%"
								title="Necessita de transporte?"
								values={transportOptions}
								name={needTransportField}
								control={control}
								valueAsBoolean
								disabled={
									watchCoverageType !== Coverage.STANDARD &&
									watchCoverageType !== Coverage.FROM_ANOTHER_AREA
								}
							/>
						</div>
						<TrashButton onClick={() => remove(idx)} />
					</div>
				</div>
			</div>
			{transportNeeded &&
				watchStartDate &&
				watchEndDate &&
				transportDays.length > 0 && (
					<TransportSection
						transportData={{
							days: transportDays,
							locations,
							transportTypeOptions,
							repeatLocation,
						}}
						isTransportExpanded={isTransportExpanded}
						onTransportExpandedChange={setIsTransportExpanded}
						onUpdateTransportDay={handleUpdateTransportDay}
						onRepeatLocationChange={handleRepeatLocationChange}
						passengerInfo={{
							address: passengerInfo.address,
							phone: passengerInfo.phone,
							onPhoneChange: handlePhoneChange,
							phoneRequired: false,
						}}
						isVacancyCover={true}
					/>
				)}
		</div>
	);
};
export default VacationCoverPlanPeriod;
